import { TFunction } from 'i18next';
import * as Yup from 'yup';
import libphonenumber from 'google-libphonenumber';
import { getDateForApi } from './date';

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

const objectRequired = (t: TFunction) =>
  Yup.object().nullable().required(t('errors.required')!);

const stringRequired = (t: TFunction) =>
  Yup.string().trim().required(t('errors.required')!);

const stringMin = (min: number, t: TFunction) =>
  Yup.string()
    .required()
    .min(min, t('errors.requiredMin').replaceAll('{0}', min.toString()));

const idRequired = (t: TFunction) =>
  Yup.number()
    .required(t('errors.required')!)
    .moreThan(0, t('errors.required')!);

const intOptional = (t: TFunction) => Yup.number().integer(t('errors.int')!);

const intRequired = (t: TFunction) =>
  intOptional(t).required(t('errors.required')!);

const intPositiveOrZero = (t: TFunction) =>
  intRequired(t).moreThan(-1, t('errors.required')!);

const intPositive = (t: TFunction) =>
  intRequired(t).moreThan(0, t('errors.intPositive')!);

const intPositiveOptional = (t: TFunction) =>
  intOptional(t)
    .transform((value, original) => (original === '' || original === null ? undefined : value))
    .moreThan(0, t('errors.intPositive')!)
    .nullable();

const intOptionalMinMax = (min: number, max: number, t: TFunction) =>
  intOptional(t)
    .min(min, valueFromTo(min, max, t))
    .max(max, valueFromTo(min, max, t));

const intRequiredMinMax = (min: number, max: number, t: TFunction) =>
  intRequired(t)
    .min(min, valueFromTo(min, max, t))
    .max(max, valueFromTo(min, max, t));

const numberOptional = () => Yup.number();

const numberRequired = (t: TFunction) =>
  numberOptional().required(t('errors.required')!);

const numberPositive = (t: TFunction) =>
  numberRequired(t).moreThan(0, t('errors.required')!);

const numberOptionalMinMax = (min: number, max: number, t: TFunction) =>
  numberOptional()
    .min(min, valueFromTo(min, max, t))
    .max(max, valueFromTo(min, max, t));

const numberRequiredMinMax = (min: number, max: number, t: TFunction) =>
  numberRequired(t)
    .min(min, valueFromTo(min, max, t))
    .max(max, valueFromTo(min, max, t));

const dateRequired = (t: TFunction) =>
  Yup.date().required(t('errors.required')!).nullable();

const stringDateNotPast = (t: TFunction) => {
  const today = getDateForApi(new Date());
  return Yup.string().test(
    'dateNotPast',
    t('errors.dateNotPast')!,
    (date) => !date || date >= today!
  );
};

const stringDateNotPastRequired = (t: TFunction) =>
  stringDateNotPast(t).required(t('errors.required')!);

const stringDateNotPastIfChanged = (t: TFunction, initialValue: string) => {
  const today = getDateForApi(new Date());
  return Yup.string().test(
    'dateNotPast',
    t('errors.dateNotPast')!,
    (date) => !date || date >= today! || date === initialValue
  );
};

const stringDateNotPastIfChangedRequired = (
  t: TFunction,
  initialValue: string
) =>
  stringDateNotPastIfChanged(t, initialValue).required(t('errors.required')!);

const stringDateNotPastOrToday = (t: TFunction) => {
  const today = getDateForApi(new Date());
  return Yup.string().test(
    'dateNotPastOrToday',
    t('errors.dateNotPastOrToday')!,
    (date) => !date || date > today!
  );
};

const stringDateNotPastOrTodayRequired = (t: TFunction) =>
  stringDateNotPastOrToday(t).required(t('errors.required')!);

const stringDateNotPastOrTodayIfChanged = (
  t: TFunction,
  initialValue: string
) => {
  const today = getDateForApi(new Date());
  return Yup.string().test(
    'dateNotPastOrToday',
    t('errors.dateNotPastOrToday')!,
    (date) => !date || date > today! || date === initialValue
  );
};

const stringDateNotPastOrTodayIfChangedRequired = (
  t: TFunction,
  initialValue: string
) =>
  stringDateNotPastOrTodayIfChanged(t, initialValue).required(
    t('errors.required')!
  );

const arrayNotEmpty = (t: TFunction) =>
  Yup.array().min(1, t('errors.arrayNotEmpty')!);

const phoneOptional = (t: TFunction) =>
  Yup.string()
    .nullable()
    .test('phone', t('errors.phone')!, (value) => {
      if (!value) {
        return true;
      }

      try {
        const number = phoneUtil.parse(value.trim());
        if (!phoneUtil.isValidNumber(number)) {
          return false;
        }
      } catch (err) {
        return false;
      }
      return true;
    });

const phoneRequired = (t: TFunction) =>
  phoneOptional(t).required(t('errors.required')!);

const emailOptional = (t: TFunction) =>
  Yup.string().nullable().email(t('errors.email')!);

const emailRequired = (t: TFunction) =>
  emailOptional(t).required(t('errors.required')!);

const urlOptional = (t: TFunction) => Yup.string().nullable().url(t('errors.url')!);

const urlRequired = (t: TFunction) =>
  urlOptional(t).required(t('errors.required')!);

const postalCodeOptional = (t: TFunction) =>
  Yup.string().max(14, t('errors.postalCode')!);

const postalCodeRequired = (t: TFunction) =>
  postalCodeOptional(t).required(t('errors.required')!);

const identificationNumberOptional = (t: TFunction) =>
  Yup.string()
    .test(
      'identificationNumber',
      t('errors.identificationNumber')!,
      (value) => {
        if (!value) {
          return true;
        }
        if (!/^[0-9]{6}\/[0-9]{3,4}$/.test(value)) {
          return false;
        }
        return validateIdentificationNumber(value.slice(0, 6) + value.slice(7));
      }
    )
    .nullable();

// VAT number formats of the EU member states (plus GB / XI for Northern Ireland, NO and CHE),
// used to recognize a VAT number typed into a company number field.
// In Norway and Switzerland the plain number is the company number and only the suffix (MVA / MWST)
// makes it a VAT number, so there the suffix is required — otherwise we would reject a valid company number.
const vatNoPatterns: Record<string, RegExp> = {
  AT: /^U\d{8}$/, // ATU33864707 — always starts with the letter U, then 8 digits
  BE: /^[01]\d{9}$/, // BE0417497106 — 10 digits, the first one is 0 or 1
  BG: /^\d{9,10}$/, // BG131468980 — 9 digits for companies, 10 for individuals
  CHE: /^\d{9}(MWST|TVA|IVA)$/, // CHE-116.281.710 MWST — the UID itself is the company number, the suffix marks a VAT registration
  CY: /^\d{8}[A-Z]$/, // CY90000005D — 8 digits followed by a check letter
  CZ: /^\d{8,10}$/, // CZ45274649 — 8 digits (IČO), 9 or 10 for individuals
  DE: /^\d{9}$/, // DE143454214 — 9 digits
  DK: /^\d{8}$/, // DK22756214 — 8 digits
  EE: /^\d{9}$/, // EE100220641 — 9 digits
  EL: /^\d{9}$/, // EL094019245 — 9 digits; Greece uses the EL prefix, not GR
  ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/, // ESA15075062 — 9 characters, may start and/or end with a letter
  FI: /^\d{8}$/, // FI01120389 — 8 digits
  FR: /^[A-HJ-NP-Z0-9]{2}\d{9}$/, // FR81775670417 — 2 alphanumeric characters (never I or O) + 9-digit SIREN
  GB: /^(\d{9}|\d{12}|(GD|HA)\d{3})$/, // GB123456789 — 9 digits, 12 for branch traders, GD/HA + 3 for public bodies
  HR: /^\d{11}$/, // HR81793146560 — 11 digits
  HU: /^\d{8}$/, // HU10773381 — 8 digits (first 8 of the 11-digit adószám)
  IE: /^(\d{7}[A-W]{1,2}|\d[A-W+*]\d{5}[A-W])$/, // IE6388047V, IE1234567FA — 7 digits + 1-2 letters, or the old 8-character style
  IT: /^\d{11}$/, // IT15844561009 — 11 digits
  LT: /^(\d{9}|\d{12})$/, // LT212154314 — 9 digits for legal entities, 12 for individuals
  LU: /^\d{8}$/, // LU20260743 — 8 digits
  LV: /^\d{11}$/, // LV40003032949 — 11 digits
  MT: /^\d{8}$/, // MT12826209 — 8 digits
  NL: /^\d{9}B\d{2}$/, // NL805734958B01 — 9 digits, then B, then 2 digits
  NO: /^\d{9}MVA$/, // NO974760673MVA — the 9-digit organisasjonsnummer is the company number, MVA marks a VAT registration
  PL: /^\d{10}$/, // PL7342867148 — 10 digits
  PT: /^\d{9}$/, // PT500100144 — 9 digits
  RO: /^\d{2,10}$/, // RO9010105 — 2 to 10 digits
  SE: /^\d{12}$/, // SE556703748501 — 12 digits, the last two are the branch number
  SI: /^\d{8}$/, // SI82646716 — 8 digits
  SK: /^\d{10}$/, // SK2020310578 — 10 digits
  XI: /^(\d{9}|\d{12}|(GD|HA)\d{3})$/, // XI197211009 — Northern Ireland, same structure as GB
};

const looksLikeVatNo = (value: string): boolean => {
  const normalized = value.replace(/[\s.-]/g, '').toUpperCase();
  // most prefixes are two letters, Switzerland uses the three letter CHE
  return [3, 2].some((prefixLength) => {
    const pattern = vatNoPatterns[normalized.slice(0, prefixLength)];
    return !!pattern && pattern.test(normalized.slice(prefixLength));
  });
};

const companyNoOptional = (t: TFunction) =>
  Yup.string()
    .max(14, t('errors.companyNo')!)
    .test('companyNoIsVatNo', t('errors.companyNoIsVatNo')!, (value) => !value || !looksLikeVatNo(value));

const companyNoRequired = (t: TFunction) =>
  companyNoOptional(t).required(t('errors.required')!);

const companyVatNoOptional = (t: TFunction) =>
  Yup.string().max(14, t('errors.companyVatNo')!);

const companyVatNoRequired = (t: TFunction) =>
  companyVatNoOptional(t).required(t('errors.required')!);

const validateIdentificationNumber = (value: string): boolean => {
  try {
    const number = value.trim();

    if (number.length !== 9 && number.length !== 10) return false;

    const day = parseInt(number.slice(4, 6));
    const ending = number.slice(6);
    if (day < 1 || day > 31) return false;
    if (ending === '000') return false;

    if (number.length === 9 && parseInt(number.slice(0, 2)) >= 54) {
      return false;
    }

    if (number.length === 10) {
      const numberBase = parseInt(number.slice(0, -1));
      const lastDigit = parseInt(number[number.length - 1]);
      const residue = numberBase % 11;

      if (residue === 10) {
        return lastDigit === 0;
      }
      return numberBase % 11 === lastDigit;
    }
    return true;
  } catch (err) {}
  return false;
};

const passwordStrength = (
  t: TFunction,
  minLength: number,
  minDigitsCount: number,
  minCapitalLetters: number,
  minLowerCaseLetters: number
) =>
  stringRequired(t).test(
    'passwordStrength',
    t('errors.passNotStrong')!,
    (value) => {
      if (!value) {
        return false;
      }

      try {
        if (value.length < minLength) {
          return false;
        }

        if (value.replace(/\D/g, '').length < minDigitsCount) {
          return false;
        }

        if (value.replace(/[^A-Z]/g, '').length < minCapitalLetters) {
          return false;
        }

        if (value.replace(/[^a-z]/g, '').length < minLowerCaseLetters) {
          return false;
        }

        return true;
      } catch (err) {
        return false;
      }
    }
  );

const valueFromTo = (min: number, max: number, t: TFunction) =>
  t('errors.valueFromTo')
    .replaceAll('{0}', min.toString())
    .replaceAll('{1}', max.toString());

export const validations = {
  objectRequired,
  stringRequired,
  stringMin,
  idRequired,
  intOptional,
  intRequired,
  intPositiveOrZero,
  intPositive,
  intPositiveOptional,
  intOptionalMinMax,
  intRequiredMinMax,
  numberOptional,
  numberRequired,
  numberPositive,
  numberOptionalMinMax,
  numberRequiredMinMax,
  dateRequired,
  stringDateNotPast,
  stringDateNotPastRequired,
  stringDateNotPastIfChanged,
  stringDateNotPastIfChangedRequired,
  stringDateNotPastOrToday,
  stringDateNotPastOrTodayRequired,
  stringDateNotPastOrTodayIfChanged,
  stringDateNotPastOrTodayIfChangedRequired,
  arrayNotEmpty,
  phoneOptional,
  phoneRequired,
  emailOptional,
  emailRequired,
  urlOptional,
  urlRequired,
  postalCodeOptional,
  postalCodeRequired,
  identificationNumberOptional,
  companyNoOptional,
  companyNoRequired,
  companyVatNoOptional,
  companyVatNoRequired,
  passwordStrength,
};
